import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Alert, Button, Card, EmptyState, Field, Spinner, StatusPill, formatDate, formatTime } from '../../components/ui';
import { useToast } from '../../components/Toast';

const REQUESTABLE = ['assigned', 'confirmed'];

export default function NannyLeaveRequests() {
  const { t } = useTranslation();
  const toast = useToast();
  const [bookings, setBookings] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  function load() {
    Promise.all([api.get('/api/nanny/bookings'), api.get('/api/nanny/leave-requests')])
      .then(([bookingsRes, leaveRes]) => {
        setBookings(bookingsRes.upcoming.filter((b) => REQUESTABLE.includes(b.status)));
        setLeaveRequests(leaveRes.leaveRequests);
      })
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit(event) {
    event.preventDefault();
    if (!selected.size) {
      toast.error(t('nannyLeave.chooseShift'));
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/nanny/leave-requests', { bookingIds: [...selected], reason });
      toast.success(t('nannyLeave.sent'));
      setSelected(new Set());
      setReason('');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!bookings || !leaveRequests) return <Spinner />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('nannyLeave.pageTitle')}</h1>
          <p className="muted">{t('nannyLeave.subtitle')}</p>
        </div>
      </div>

      <Card title={t('nannyLeave.requestTimeOff')}>
        {bookings.length ? (
          <form onSubmit={submit}>
            <ul className="date-list">
              {bookings.map((b) => (
                <li key={b.id}>
                  <label className="check">
                    <input type="checkbox" checked={selected.has(b.id)} onChange={() => toggle(b.id)} />
                    {formatDate(b.bookingDate, { long: true })} · {formatTime(b.startTime)} – {formatTime(b.endTime)} ·{' '}
                    {b.family.familyName || b.family.name}
                  </label>
                </li>
              ))}
            </ul>
            <Field label={t('nannyLeave.reason')} required hint={t('nannyLeave.reasonHint')}>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} required />
            </Field>
            <div className="form-footer">
              <Button type="submit" loading={busy}>{t('nannyLeave.sendRequest')}</Button>
            </div>
          </form>
        ) : (
          <EmptyState title={t('nannyLeave.noShifts')} />
        )}
      </Card>

      <Card title={t('nannyLeave.yourRequests', { count: leaveRequests.length })}>
        {leaveRequests.length ? (
          leaveRequests.map((lr) => (
            <article key={lr.id} className="booking-card">
              <header>
                <div>
                  <strong>{lr.bookings.map((b) => formatDate(b.bookingDate)).join(', ')}</strong>
                </div>
                <StatusPill status={lr.status} label={lr.statusLabel} />
              </header>
              <p className="note-block"><strong>{t('nannyLeave.yourReason')}</strong> {lr.reason}</p>
              {lr.adminNotes && <p className="note-block"><strong>{t('nannyLeave.officeNote')}</strong> {lr.adminNotes}</p>}
            </article>
          ))
        ) : (
          <EmptyState title={t('nannyLeave.noRequestsYet')} />
        )}
      </Card>
    </>
  );
}
